import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { User, UserBadgeCheck } from 'iconoir-react'
import { ReactNode, useEffect, useState } from 'react'
import { InlineAddChip } from 'app/components/shared/InlineAddActionLink'
import { cellChevronClasses, EDITABLE_CELL_HOVER } from 'app/components/table/cellInteraction'
import ComboAsync from 'app/components/ui/combo/combo-async'
import { StaffForm } from 'app/components/staff/StaffForm'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import type { StaffJson, StaffOptionValue } from 'app/types/entities'
import type { ActivityOwner } from 'app/generated/graphql'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { showModal } from 'app/lib/modal'
import { OptionType } from 'types/OptionType'

type StaffGql = { id: string; name: string; userId?: string }

// Compile-time guard: the leadOwner prop accepts ActivityOwner because it is
// structurally a StaffSummary (the base of StaffOptionValue). If a future schema
// regeneration makes ActivityOwner diverge on the fields this component reads,
// this alias resolves to `never` and the annotated const below fails to compile,
// surfacing the drift instead of failing silently downstream.
type OwnerCompatible = ActivityOwner extends Pick<StaffOptionValue, 'id' | 'name'> ? true : never
const _ownerCompatible: OwnerCompatible = true

const staffOptionMeta = {
  nameInitial: true,
  borderClassName: `border border-strong`,
  backgroundClassName: `bg-surface-strong`,
  textClassName: `text-strong font-bold`,
}

/** Build the dropdown option for a Staff member. Consumers read `value`. */
function toStaffOption(id: string, name: string): OptionType {
  return { id: name, name, value: { id, name }, meta: staffOptionMeta }
}

type Props = {
  // Accepts an activity's owner (ActivityOwner) as well — structurally a StaffSummary,
  // which StaffOptionValue extends. The extra StaffOptionValue fields are optional.
  leadOwner?: StaffOptionValue | ActivityOwner
  locationId?: string
  workspaceId?: string
  firstNameInitial?: boolean
  name?: string
  className?: string
  customIcon?: ReactNode
  placeholder?: string
  inputClassName?: string
  chevronClassName?: string
  /** Drop the trailing chevron on a filled cell. It reveals on `group-hover/cell`,
   *  a scope only a table `td` provides, so outside a table it would stay
   *  permanently invisible while still eating its width and truncating the name. */
  hideChevron?: boolean
  useSingleFieldInput?: boolean
  onChange?: (val?: { id: string; name: string; value: StaffOptionValue }) => void
}

export function StaffAssignCell(props: Props) {
  const [leadOwner, setLeadOwner] = useState(props?.leadOwner)
  const [isMatched, setIsMatched] = useState(false)
  // Staff created inline from this dropdown. The staffMembers search index lags
  // creation by a few seconds, so a refetch alone would not list the new record.
  // ComboAsync merges additionalChoices into every result and dedupes by id.
  const [createdStaffOptions, setCreatedStaffOptions] = useState<OptionType[]>([])
  const checkPermission = useCheckPermission()
  const canCreateStaff = checkPermission([`staff:manage`])
  const hasName = !!leadOwner?.name

  useEffect(() => setLeadOwner(props?.leadOwner), [props?.leadOwner])

  useEffect(() => setIsMatched(Boolean(leadOwner?.userId)), [leadOwner?.userId])

  // The chevron is rendered here rather than passed as chevronClassName because
  // ComboBoxView drops chevronClassName entirely on its customButton branch, so a
  // filled cell would otherwise read as static text. Empty cells have no
  // customButton and keep using the prop.
  const staffIcon = hasName ? (
    <>
      <div
        className={`flex min-w-0 items-center gap-2 rounded border border-default px-2 py-0.5 ${
          isMatched ? `text-green-600` : `text-default`
        }`}
      >
        {isMatched ? <UserBadgeCheck className="h-4 w-4 shrink-0" /> : <User className="h-4 w-4 shrink-0" />}
        <span className="min-w-0 truncate text-xs font-medium">{leadOwner?.name}</span>
      </div>
      {!props.hideChevron && <ChevronDownIcon className={cellChevronClasses} />}
    </>
  ) : undefined

  const fetcher = async (params: { query: string; [key: string]: string }) => {
    const result = await gqlFetchList<StaffGql>(
      `staffMembers`,
      {
        search: params.query || undefined,
        pagination: { limit: 25 },
      },
      `id name userId`,
    )
    return result
  }

  /**
   * Select a Staff member that was just created from the dropdown's "+ Add Staff"
   * modal. The option passed to onChange has the exact shape optionsMapper builds
   * for fetched options, so consumers persist it through their existing path.
   */
  const selectCreatedStaff = (staff?: Partial<StaffJson>) => {
    if (!staff?.id || !staff?.name) return
    const { id, name } = staff
    const option = toStaffOption(id, name)
    setLeadOwner({ id, name })
    setCreatedStaffOptions((options) => [...options.filter((o) => o.id !== option.id), option])
    props?.onChange?.(option)
  }

  const openStaffModal = () => {
    showModal(<StaffForm onSubmit={(data) => selectCreatedStaff(data?.staff)} />, {
      title: `Add Staff`,
      isFullScreen: false,
      removePadding: true,
      useRadix: true,
    })
  }

  return (
    <span className={props.className ?? `min-w-0 w-full`}>
      <ComboAsync
        name={props.name}
        useSingleFieldInput={props.useSingleFieldInput}
        // ComboAsync hangs the tint's `relative h-full` wrapper off this class,
        // so it is the one element that spans the whole cell, which also makes it
        // the right place to clip the "+ Assign" chip when the verb expands wider
        // than the column. The options list is a portaled Radix popover, so it is
        // unaffected.
        className={`min-w-0 overflow-hidden px-2 ${EDITABLE_CELL_HOVER}`}
        // An unassigned cell would otherwise render completely blank, with only a
        // hover chevron hinting it is editable. "Assign staff" (not "Add Staff")
        // because picking an existing member is the primary action here; creating
        // one is the fallback at the bottom of the dropdown. Hosts that want the
        // bare look, like the contact drawer, pass placeholder="" to opt out.
        placeholder={props.placeholder ?? `Assign staff`}
        inputClassName={props.inputClassName ?? `h-full! border-0! shadow-none! py-0! pl-0! pr-5! cursor-pointer`}
        chevronClassName="hidden group-hover/cell:block [&_.chevron-icon]:h-3 [&_.chevron-icon]:w-3 [&_.chevron-icon]:text-minimal"
        showSearchInDropdown={true}
        customButton={hasName ? staffIcon : undefined}
        customButtonClassName="flex h-full min-w-0 cursor-pointer items-center"
        customIcon={hasName ? undefined : (props.customIcon ?? <InlineAddChip verb="Assign" />)}
        value={hasName ? { name: ``, id: leadOwner?.name ?? ``, value: leadOwner?.name ?? `` } : undefined}
        fetcher={fetcher}
        optionsMapper={(res) => {
          return res?.hits?.map((hit: StaffGql) => toStaffOption(hit.id, hit.name))
        }}
        additionalChoices={createdStaffOptions}
        additionalParams={{
          locationId: props?.locationId!,
          workspaceId: props?.workspaceId!,
        }}
        onChange={(val) => {
          if (leadOwner?.id === val?.value?.id || leadOwner?.name === val?.value?.name) {
            setLeadOwner(undefined)
            props?.onChange?.()
          } else {
            setLeadOwner({ id: val?.value?.id, name: val?.value?.name })
            props?.onChange?.(val)
          }
        }}
        addText="Add Staff"
        onAddNewValue={canCreateStaff ? openStaffModal : undefined}
        allowDeselectOption
      />
    </span>
  )
}
